import { useEffect, useMemo, useRef, useState } from "react";
import type React from "react";
import { useSearchParams } from "react-router-dom";
import { Paperclip, Camera } from "lucide-react";
import { theme, card, emptyFrame, floatingSheet, btnPrimary, inputStyle } from "../theme";
import { LoadingBlock } from "../components/Spinner";
import { api, ApiError, apiErrorMessage } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { fmtBytes } from "../utils/format";
import { fmtDateOnly } from "../lib/dateOnly";
import { useFrameworks, FRAMEWORK_LABELS, FRAMEWORK_ORDER } from "../context/FrameworksContext";
import { EntityTagsRow, EntityTagFilters, useEntityTagData, useTagFilter, matchesEntityTagFilter, entityRef, KIND_HREFS } from "../components/EntityTags";
import { RelatedRecords, type RelatedLink } from "../components/RelatedRecords";
import { EvidenceImportPanel, EvidenceSourceBadge } from "../components/EvidenceImportPanel";
import { useDeepLinkScroll } from "../hooks/useDeepLinkScroll";
import { IndexTabs } from "../components/Drawing";
import { useControlOptions, type ControlOption } from "../components/ControlPicker";
import { frameworkLabel } from "../nav/frameworkMeta";
import { displayControlRef, displayControlTitle } from "../lib/controlRef";
import {
  ValidityChip, ValidityFields, suggestValidity, useEvidenceTypes, useRenewalOwners, validityPayload,
  type DocumentValidity, type ValidityDraft, type ValiditySuggestion,
} from "../components/EvidenceValidity";
import { QualityChip, QualityReviewPanel, QualityReviewSettingsLine, type QualityReview, type QualityReviewSettings } from "../components/EvidenceQualityReview";

// ── Types ─────────────────────────────────────────────────────────────────

interface DocVersion {
  id: string;
  fileName: string;
  sizeBytes: number;
  uploadedAt: string;
  version: number;
}

interface DocTag {
  id: string;
  documentId: string;
  frameworkId: string;
  controlRef: string;
}

/** A tag is a control of one standard: its key is the pair, never the bare id. */
const tagKey = (frameworkId: string, controlRef: string) => `${frameworkId}:${controlRef}`;

const standardLabel = (frameworkId: string) =>
  frameworkId === "legal" ? "Legal obligations" : frameworkLabel(frameworkId);

/** A legal obligation as a person reads it: the law and its article ("NIS2 Directive: Art. 21(2)(e)"). */
type LegalRef = { law: string; ref: string };
function legalRefOf(option: ControlOption): LegalRef | null {
  if (option.frameworkId !== "legal" || !option.domain) return null;
  const ref = option.name.split(/\s+[\u2014\u2013-]\s+/)[0]?.trim() || option.name;
  return { law: option.domain, ref };
}

/** A tag as a person reads it: the standard, then the standard's own reference. */
function TagChip({ tag, legal }: { tag: { frameworkId: string; controlRef: string }; legal?: LegalRef | null }) {
  // A legal obligation's id ("legal-nis2-art21e") names no law; its catalogue row does (DOC-41).
  const standard = legal?.law ?? standardLabel(tag.frameworkId);
  const ref = legal?.ref ?? displayControlRef(tag.controlRef);
  return (
    <span
      title={`${standard} ${ref}`}
      style={{
        fontVariantNumeric: "tabular-nums", fontSize: 10, padding: "2px 6px",
        background: theme.color.accentBg, color: theme.color.accentText,
        borderRadius: 4,
      }}
    >
      <span style={{ opacity: 0.65 }}>{standard}: </span>
      {ref}
    </span>
  );
}

interface EvidenceDoc {
  id: string;
  fileName: string;
  sizeBytes: number;
  uploadedAt: string;
  version: number;
  documentGroupId: string;
  versions: DocVersion[]; // older versions, newest-first (current excluded)
  controls: DocTag[];
  source?: string;              // upload | m365 | onedrive | sharepoint | google_drive | dora_register | resilience_register | risk_register | tprm_register | dp_register | threat_register
  sourceWebUrl?: string | null; // link back to the file at its source, for imported documents
  siteId?: string | null;       // where the evidence was gathered; null = organisation-wide
  site?: { id: string; name: string; country: string } | null;
  // What it is linked to, for the Related records panel.
  riskLinks?: Array<{ riskId: string; risk: { id: string; title: string; displayId: number | null; status: string } }>;
  policyTemplate?: { id: string; title: string } | null;
  evidenceRequestAnswers?: Array<{ attachedAt: string; request: { id: string; controlId: string; status: string; bureau: { name: string } } }>;
  // Validity of the latest version (backend lib/evidenceValidity.ts); null when it has no type.
  evidenceType?: string | null;
  evidenceDate?: string | null;
  renewalOwnerUserId?: string | null;
  renewalOwner?: { id: string; displayName: string | null; email: string; isActive: boolean } | null;
  validity?: DocumentValidity | null;
  // The latest version's quality review on filing (backend lib/evidenceQuality.ts); advice only.
  qualityReview?: QualityReview | null;
}

const NO_VALIDITY: ValidityDraft = { evidenceType: "", evidenceDate: "", renewalOwnerUserId: "" };

/** The document's own links: risks it evidences, the policy it is the adopted copy of, the auditor requests it answered. */
function documentLinks(doc: EvidenceDoc): RelatedLink[] {
  return [
    ...(doc.riskLinks ?? []).map((l) => ({ kindLabel: "Risk", label: `${l.risk.displayId != null ? `RISK-${String(l.risk.displayId).padStart(3, "0")} ` : ""}${l.risk.title}`, href: KIND_HREFS.risk(l.risk.id) })),
    ...(doc.policyTemplate ? [{ kindLabel: "Policy", label: `Adopted copy of ${doc.policyTemplate.title}`, href: "/templates" }] : []),
    ...(doc.site ? [{ kindLabel: "Site", label: doc.site.name, href: `/sites/${doc.site.id}` }] : []),
    ...(doc.evidenceRequestAnswers ?? []).map((a) => ({ kindLabel: "Evidence request", label: `Sent to ${a.request.bureau.name} for ${a.request.controlId}`, href: `/auditor-feedback?request=${encodeURIComponent(a.request.id)}` })),
  ];
}

/** The browser's direct PUT to Blob Storage failed (CORS, network, or a non-2xx from storage). */
class StorageUploadError extends Error {
  constructor() {
    super("The file could not be sent to storage. Please try again.");
  }
}

/**
 * What to show when an upload step fails. The API explains what it rejected
 * (file type, size cap, missing permission) in `message`; storage failures
 * carry their own line; anything else gets the caller's generic fallback.
 */
function uploadErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof StorageUploadError) return e.message;
  if (e instanceof ApiError) {
    const payload = e.payload as { message?: unknown } | null;
    if (typeof payload?.message === "string") return payload.message;
  }
  return fallback;
}

interface UploadModalState {
  file: File;
  blobPath: string;
  step: "uploading" | "suggesting" | "selecting" | "confirming";
  suggestedTags: DocTag[];
  selectedTags: DocTag[];
  controlSearch: string;
  /** The standard the control list is narrowed to; "" = every enrolled standard. */
  controlFramework: string;
  error: string;
  newVersionOf?: string; // document ID when uploading a new version
  inheritedTags?: DocTag[]; // tags inherited from current version when uploading a new version
  /** Type, evidence date and renewal owner; a new version keeps the type and owner and asks only the date. */
  validity: ValidityDraft;
  validitySuggestion: ValiditySuggestion | null;
  /** The type the replaced version carries, when it has one. */
  inheritedType?: string | null;
}

interface ValidityModalState {
  doc: EvidenceDoc;
  draft: ValidityDraft;
  saving: boolean;
  error?: string | null;
}

interface EditModalState {
  doc: EvidenceDoc;
  selectedTags: DocTag[];
  controlSearch: string;
  controlFramework: string;
  saving: boolean;
  error?: string | null;
}

// ── Shared styles ─────────────────────────────────────────────────────────

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.6)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};

const modalCard: React.CSSProperties = {
  ...floatingSheet,
  padding: 24,
  maxWidth: 520,
  width: "90%",
  maxHeight: "80vh",
  overflowY: "auto",
};

const secondaryBtn: React.CSSProperties = {
  background: "transparent",
  border: `1px solid ${theme.color.borderStrong}`,
  borderRadius: 3,
  padding: "6px 12px",
  fontSize: 12,
  color: theme.color.muted,
  cursor: "pointer",
};

const dangerBtn: React.CSSProperties = {
  background: "transparent",
  border: `1px solid ${theme.color.danger}`,
  borderRadius: 3,
  padding: "6px 12px",
  fontSize: 12,
  color: theme.color.dangerText,
  cursor: "pointer",
};

// ── Sub-components ────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div
      className="doc-spinner"
      style={{
        width: 24,
        height: 24,
        border: `3px solid ${theme.color.border}`,
        borderTopColor: theme.color.accent,
        borderRadius: "50%",
        margin: "0 auto",
      }}
    />
  );
}

function ControlList({
  controls,
  selectedKeys,
  onToggle,
  search,
  onSearchChange,
  framework,
  onFrameworkChange,
}: {
  /** The enrolled standards' controls (useControlOptions): one list, several standards. */
  controls: ControlOption[];
  /** tagKey(frameworkId, controlRef) of every ticked control. */
  selectedKeys: Set<string>;
  onToggle: (control: ControlOption) => void;
  search: string;
  onSearchChange: (v: string) => void;
  framework: string;
  onFrameworkChange: (v: string) => void;
}) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const standards = [...new Set(controls.map((c) => c.frameworkId))];
  const q = search.toLowerCase();
  const filtered = controls.filter(
    (c) =>
      (!framework || c.frameworkId === framework) &&
      (c.id.toLowerCase().includes(q) ||
        displayControlRef(c.id).toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q))
  );

  return (
    <div>
      <div style={{ display: "flex", gap: 8 }}>
        <input
          style={{ ...inputStyle, flex: 1 }}
          placeholder="Search controls…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        {standards.length > 1 && (
          <select
            aria-label="Standard"
            style={{ ...inputStyle, width: "auto", maxWidth: 200 }}
            value={framework}
            onChange={(e) => onFrameworkChange(e.target.value)}
          >
            <option value="">All standards</option>
            {standards.map((fw) => <option key={fw} value={fw}>{standardLabel(fw)}</option>)}
          </select>
        )}
      </div>
      <div style={{ maxHeight: 240, overflowY: "auto", marginTop: 8 }}>
        {filtered.map((c) => {
          const key = tagKey(c.frameworkId, c.id);
          const ref = displayControlRef(c.id);
          return (
            <label
              key={key}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 8px",
                borderRadius: 3,
                cursor: "pointer",
                background: hoveredId === key ? theme.color.panelAlt : "transparent",
              }}
              onMouseEnter={() => setHoveredId(key)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <input
                type="checkbox"
                checked={selectedKeys.has(key)}
                onChange={() => onToggle(c)}
                style={{ cursor: "pointer", flexShrink: 0 }}
              />
              <span style={{ fontSize: 12, color: theme.color.text }}>
                {!framework && standards.length > 1 && (
                  <span style={{ color: theme.color.faint }}>{standardLabel(c.frameworkId)} </span>
                )}
                <span style={{ fontVariantNumeric: "tabular-nums", color: theme.color.accentText }}>{ref}</span>
                {" — "}
                {displayControlTitle(c.id, c.name, ref)}
              </span>
            </label>
          );
        })}
        {filtered.length === 0 && (
          <div style={{ padding: "12px 8px", fontSize: 12, color: theme.color.faint }}>
            No controls match
          </div>
        )}
      </div>
    </div>
  );
}

// ── Evidence Repository: docs grouped by standard ─────────────────────────

function DocCard({
  doc,
  canWrite,
  onDownload,
  onDelete,
  onNewVersion,
  onEditTags,
}: {
  doc: EvidenceDoc;
  canWrite: boolean;
  onDownload: (id: string) => void;
  onDelete: (doc: EvidenceDoc) => void;
  onNewVersion: (doc: EvidenceDoc) => void;
  onEditTags: (doc: EvidenceDoc) => void;
}) {
  return (
    <div style={{ ...card, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading, wordBreak: "break-all", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          {doc.fileName}
          {doc.versions.length > 0 && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: 3,
              background: theme.color.accentBg, color: theme.color.accentText, border: `1px solid ${theme.color.accent}`,
            }}>
              v{doc.version}
            </span>
          )}
          <EvidenceSourceBadge source={doc.source} webUrl={doc.sourceWebUrl} />
          <ValidityChip validity={doc.validity} />
        </div>
        <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 2 }}>
          {fmtBytes(doc.sizeBytes)} · {new Date(doc.uploadedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
        {canWrite && <button style={secondaryBtn} onClick={() => onNewVersion(doc)} title="Upload new version">New ver.</button>}
        {canWrite && <button style={secondaryBtn} onClick={() => onEditTags(doc)}>Tags</button>}
        <button style={secondaryBtn} onClick={() => onDownload(doc.id)}>Download</button>
        {canWrite && <button style={dangerBtn} onClick={() => onDelete(doc)}>Delete</button>}
      </div>
    </div>
  );
}

function EvidenceRepositoryView({
  canWrite,
  documents,
  frameworks,
  onDownload,
  onDelete,
  onNewVersion,
  onEditTags,
  onUploadForFramework,
}: {
  documents: EvidenceDoc[] | null;
  frameworks: string[];
  onDownload: (id: string) => void;
  onDelete: (doc: EvidenceDoc) => void;
  onNewVersion: (doc: EvidenceDoc) => void;
  onEditTags: (doc: EvidenceDoc) => void;
  onUploadForFramework: (frameworkId: string) => void;
  canWrite: boolean;
}) {
  if (!documents) {
    return <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading…</div>;
  }

  // Build a map: frameworkId → docs tagged to that framework
  const byFramework = new Map<string, EvidenceDoc[]>();
  const untagged: EvidenceDoc[] = [];

  for (const doc of documents) {
    if (doc.controls.length === 0) {
      untagged.push(doc);
      continue;
    }
    const fwIds = [...new Set(doc.controls.map((c) => c.frameworkId))];
    for (const fwId of fwIds) {
      if (!byFramework.has(fwId)) byFramework.set(fwId, []);
      byFramework.get(fwId)!.push(doc);
    }
  }

  // Show enrolled frameworks first (in order), then any others
  const orderedFrameworks = [
    ...FRAMEWORK_ORDER.filter((id) => frameworks.includes(id)),
    ...[...byFramework.keys()].filter((id) => !FRAMEWORK_ORDER.includes(id as never)),
  ];

  const sections = orderedFrameworks.map((fwId) => ({
    fwId,
    label: FRAMEWORK_LABELS[fwId as keyof typeof FRAMEWORK_LABELS] ?? fwId,
    docs: byFramework.get(fwId) ?? [],
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {sections.map(({ fwId, label, docs }) => (
        <div key={fwId}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading }}>{label}</div>
              <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 2 }}>
                {docs.length === 0 ? "No evidence uploaded yet" : `${docs.length} document${docs.length !== 1 ? "s" : ""}`}
              </div>
            </div>
            {canWrite && (
              <button
                style={{ ...secondaryBtn, fontSize: 12 }}
                onClick={() => onUploadForFramework(fwId)}
              >
                + Add evidence
              </button>
            )}
          </div>

          {docs.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {docs.map((doc) => (
                <DocCard
                  key={doc.id}
                  doc={doc}
                  onDownload={onDownload}
                  canWrite={canWrite}
                  onDelete={onDelete}
                  onNewVersion={onNewVersion}
                  onEditTags={onEditTags}
                />
              ))}
            </div>
          ) : (
            <div
              style={{
                ...emptyFrame,
                padding: "28px 24px",
                color: theme.color.faint,
                fontSize: 12,
              }}
            >
              No evidence files tagged to {label} yet.
              <br />
              Upload evidence from the <strong>All Documents</strong> tab and tag it to this standard.
            </div>
          )}
        </div>
      ))}

      {untagged.length > 0 && (
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 10 }}>
            Untagged / General
            <span style={{ fontWeight: 400, fontSize: 11, color: theme.color.faint, marginLeft: 8 }}>
              {untagged.length} document{untagged.length !== 1 ? "s" : ""} — not yet assigned to a standard
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {untagged.map((doc) => (
              <DocCard
                key={doc.id}
                doc={doc}
                onDownload={onDownload}
                canWrite={canWrite}
                onDelete={onDelete}
                onNewVersion={onNewVersion}
                onEditTags={onEditTags}
              />
            ))}
          </div>
        </div>
      )}

      {documents.length === 0 && (
        <div style={{ color: theme.color.faint, fontSize: 13, textAlign: "center", paddingTop: 20 }}>
          No evidence documents uploaded yet. Go to <strong>All Documents</strong> to upload your first file.
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────

export function DocumentsPage() {
  const { frameworks } = useFrameworks();
  const [documents, setDocuments] = useState<EvidenceDoc[] | null>(null);
  // Only the enrolled standards' controls (plus the legal obligations), each
  // carrying its own standard, so a tag is saved with its real frameworkId.
  const { options: controlOptions } = useControlOptions();
  const legalRefs = useMemo(() => {
    const m = new Map<string, LegalRef>();
    for (const o of controlOptions) { const r = legalRefOf(o); if (r) m.set(o.id, r); }
    return m;
  }, [controlOptions]);
  // "+ Add evidence" under a standard narrows the upload's control list to it.
  const uploadFrameworkRef = useRef("");
  const [dragOver, setDragOver] = useState(false);
  const [uploadModal, setUploadModal] = useState<UploadModalState | null>(null);
  const [editModal, setEditModal] = useState<EditModalState | null>(null);
  const [validityModal, setValidityModal] = useState<ValidityModalState | null>(null);
  const evidenceTypes = useEvidenceTypes();
  const renewalOwners = useRenewalOwners();
  const [historyOpen, setHistoryOpen] = useState<string | null>(null); // documentGroupId
  const [activeTab, setActiveTab] = useState<"library" | "repository">("library");
  const [error, setError] = useState("");
  const [sites, setSites] = useState<Array<{ id: string; name: string; country: string }>>([]);
  // Departments, offices, jurisdictions and types all come through the one tag
  // row now (components/EntityTags.tsx), which loads them once for the page.
  const tagData = useEntityTagData();
  const [tagFilter, setTagFilter] = useTagFilter();
  // ?site=<id> (from a site's detail page) opens the repository filtered to the
  // evidence gathered there; "none" keeps organisation-wide evidence only.
  const [searchParams] = useSearchParams();
  // ?document=<id> (tag cross-references, a site's detail page, a control's
  // evidence) brings that document into view — any version of it, since a link
  // may have been made before a newer version was uploaded. Filters are left
  // wide open so the document cannot be hidden by one.
  const documentParam = searchParams.get("document");
  const [siteFilter, setSiteFilter] = useState(() => documentParam ? "all" : searchParams.get("site") ?? "all");
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Mobile evidence capture: a second input with `capture` opens the rear
  // camera directly on phones/tablets instead of the file picker.
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const supportsCapture = typeof navigator !== "undefined" && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const newVersionTargetRef = useRef<EvidenceDoc | null>(null);
  const { user } = useCurrentUser();
  // Where an uploaded document is mirrored: the storage connection when there is one
  // (documents.routes.ts mirrors uploads there), otherwise the Microsoft 365 folder.
  // Re-sync is POST /storage|m365/sync/:id, behind documents:write.
  const [mirrorTarget, setMirrorTarget] = useState<{ path: "storage" | "m365"; label: string } | null>(null);
  const [resyncing, setResyncing] = useState<string | null>(null);
  const [notice, setNotice] = useState("");
  // Uploading, tagging, new versions and deleting all need documents:write on the server; the
  // controls are offered only to members who hold it (DOC-1).
  const canWriteDocs = !!user?.permissions?.includes("documents:write") && user?.role !== "auditor";
  const canResync = canWriteDocs;
  const canAdminOrg = !!user?.permissions?.includes("org:admin") && user?.role !== "auditor";
  const [qualitySettings, setQualitySettings] = useState<QualityReviewSettings | null>(null);

  useEffect(() => {
    Promise.all([
      api.get<{ provider: string | null; authError?: unknown }>("/storage/status").catch(() => ({ provider: null, authError: null })),
      api.get<{ connected: boolean }>("/m365/status").catch(() => ({ connected: false })),
    ]).then(([storage, m365]) => {
      if (storage.provider) setMirrorTarget({ path: "storage", label: "connected storage" });
      else if (m365.connected) setMirrorTarget({ path: "m365", label: "Microsoft 365" });
    });
  }, []);

  async function resyncDoc(doc: EvidenceDoc) {
    if (!mirrorTarget) return;
    setResyncing(doc.id);
    setError("");
    setNotice("");
    try {
      await api.post(`/${mirrorTarget.path}/sync/${doc.id}`, {});
      setNotice(`"${doc.fileName}" re-synced to ${mirrorTarget.label}.`);
    } catch (e) {
      setError(apiErrorMessage(e, `Could not re-sync "${doc.fileName}" to ${mirrorTarget.label}.`));
    } finally {
      setResyncing(null);
    }
  }

  async function load() {
    const [docsData, sitesData] = await Promise.all([
      api.get<{ documents: EvidenceDoc[]; qualityReview?: QualityReviewSettings }>("/documents"),
      api.get<{ sites: Array<{ id: string; name: string; country: string }> }>("/sites").catch(() => ({ sites: [] })),
    ]);
    setDocuments(docsData.documents);
    setQualitySettings(docsData.qualityReview ?? null);
    setSites(sitesData.sites);
  }

  useEffect(() => { load(); }, []);

  async function startUpload(file: File, newVersionOf?: EvidenceDoc) {
    setError("");
    const inheritedTags = newVersionOf?.controls ?? [];
    const controlFramework = newVersionOf ? "" : uploadFrameworkRef.current;
    uploadFrameworkRef.current = "";
    setUploadModal({
      file,
      blobPath: "",
      step: "uploading",
      suggestedTags: [],
      selectedTags: inheritedTags,
      controlSearch: "",
      controlFramework,
      error: "",
      newVersionOf: newVersionOf?.id,
      inheritedTags,
      validity: newVersionOf?.evidenceType
        ? { evidenceType: newVersionOf.evidenceType, evidenceDate: "", renewalOwnerUserId: newVersionOf.renewalOwnerUserId ?? "" }
        : { ...NO_VALIDITY, renewalOwnerUserId: user?.role === "client" ? user.id : "" },
      validitySuggestion: null,
      inheritedType: newVersionOf?.evidenceType ?? null,
    });
    // The type and date the file name points to, by rule (never the AI); the uploader confirms them.
    void suggestValidity(file.name).then((sg) => {
      if (!sg) return;
      setUploadModal((prev) => {
        if (!prev || prev.file !== file) return prev;
        const v = prev.validity;
        const evidenceType = prev.inheritedType || v.evidenceType || sg.evidenceType || "";
        return {
          ...prev,
          validitySuggestion: sg,
          validity: { ...v, evidenceType, evidenceDate: v.evidenceDate || (evidenceType ? sg.evidenceDate ?? "" : "") },
        };
      });
    });

    try {
      const { blobPath, uploadUrl } = await api.post<{ blobPath: string; uploadUrl: string }>(
        "/documents/request-upload",
        { fileName: file.name, contentType: file.type || "application/octet-stream" }
      );

      // Direct PUT to Blob Storage on the SAS URL — the file never passes through
      // our API. A CORS or network failure rejects the fetch itself, so both that
      // and a non-2xx response are reported as a storage failure.
      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "x-ms-blob-type": "BlockBlob", "Content-Type": file.type || "application/octet-stream" },
        body: file,
      }).catch(() => null);
      if (!putRes?.ok) throw new StorageUploadError();

      setUploadModal((prev) => (prev ? { ...prev, step: "suggesting", blobPath } : null));

      let suggestedTags: DocTag[] = [];
      let suggestError = "";
      try {
        const result = await api.post<{ tags: DocTag[] }>("/documents/suggest-controls", { fileName: file.name });
        suggestedTags = result.tags;
      } catch (e) {
        if (e instanceof ApiError && (e.status === 402 || e.status === 429)) {
          suggestError = e.status === 429
            ? "AI suggestions are busy — select controls manually or try again in a few minutes."
            : "AI suggestions are not available on your plan or the spend cap has been reached. Select controls manually.";
        }
      }

      setUploadModal((prev) => {
        if (!prev) return null;
        // Merge AI suggestions with inherited tags, deduping by frameworkId+controlRef
        const merged = newVersionOf
          ? [...inheritedTags, ...suggestedTags].filter(
              (tag, i, arr) => arr.findIndex((t) => t.frameworkId === tag.frameworkId && t.controlRef === tag.controlRef) === i
            )
          : suggestedTags;
        return { ...prev, step: "selecting", suggestedTags, selectedTags: merged, error: suggestError };
      });
    } catch (e) {
      setUploadModal(null);
      setError(uploadErrorMessage(e, "Upload failed. Please try again."));
    }
  }

  async function confirmUpload(tags: DocTag[]) {
    if (!uploadModal) return;
    const { blobPath, file, newVersionOf, validity } = uploadModal;
    setUploadModal((prev) => (prev ? { ...prev, step: "confirming" } : null));
    try {
      const endpoint = newVersionOf ? `/documents/${newVersionOf}/new-version` : "/documents";
      const v = validityPayload(validity);
      await api.post(endpoint, {
        blobPath,
        fileName: file.name,
        sizeBytes: file.size,
        tags: tags.map(({ frameworkId, controlRef }) => ({ frameworkId, controlRef })),
        // A new version carries its type and owner over and takes only the new date.
        ...(newVersionOf ? (v.evidenceDate ? { evidenceDate: v.evidenceDate } : {}) : v),
      });
      await load();
      setUploadModal(null);
    } catch (e) {
      setUploadModal((prev) =>
        prev ? { ...prev, step: "selecting", error: uploadErrorMessage(e, "Failed to save document. Please try again.") } : null
      );
    }
  }

  async function saveEditModal() {
    if (!editModal) return;
    setEditModal((prev) => (prev ? { ...prev, saving: true } : null));
    try {
      await api.patch(`/documents/${editModal.doc.id}/controls`, {
        tags: editModal.selectedTags.map(({ frameworkId, controlRef }) => ({ frameworkId, controlRef })),
      });
      await load();
      setEditModal(null);
    } catch (e) {
      setEditModal((prev) => (prev ? { ...prev, saving: false, error: apiErrorMessage(e, "The tags could not be saved.") } : null));
    }
  }

  async function saveValidity() {
    if (!validityModal) return;
    setValidityModal((prev) => (prev ? { ...prev, saving: true, error: null } : null));
    try {
      await api.patch(`/documents/${validityModal.doc.id}/validity`, validityPayload(validityModal.draft));
      await load();
      setValidityModal(null);
    } catch (e) {
      setValidityModal((prev) => (prev ? { ...prev, saving: false, error: apiErrorMessage(e, "The validity could not be saved.") } : null));
    }
  }

  function openValidity(doc: EvidenceDoc) {
    setValidityModal({
      doc, saving: false,
      draft: { evidenceType: doc.evidenceType ?? "", evidenceDate: doc.evidenceDate ? doc.evidenceDate.slice(0, 10) : "", renewalOwnerUserId: doc.renewalOwnerUserId ?? "" },
    });
  }

  async function downloadDoc(docId: string) {
    try {
      const { url } = await api.get<{ url: string }>(`/documents/${docId}/download-url`);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      setError("Failed to get download URL.");
    }
  }

  async function deleteDoc(doc: EvidenceDoc) {
    if (!window.confirm(`Delete "${doc.fileName}" v${doc.version}? This cannot be undone.`)) return;
    try {
      await api.del(`/documents/${doc.id}`);
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, `"${doc.fileName}" could not be deleted.`));
    }
  }

  async function deleteVersion(v: DocVersion, fileName: string) {
    if (!window.confirm(`Delete version ${v.version} of "${fileName}"?`)) return;
    try {
      await api.del(`/documents/${v.id}`);
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, `Version ${v.version} of "${fileName}" could not be deleted.`));
    }
  }

  function toggleTag(tags: DocTag[], control: ControlOption): DocTag[] {
    const key = tagKey(control.frameworkId, control.id);
    return tags.some((t) => tagKey(t.frameworkId, t.controlRef) === key)
      ? tags.filter((t) => tagKey(t.frameworkId, t.controlRef) !== key)
      : [...tags, { id: "", documentId: "", frameworkId: control.frameworkId, controlRef: control.id }];
  }

  function toggleUploadControl(control: ControlOption) {
    setUploadModal((prev) => (prev ? { ...prev, selectedTags: toggleTag(prev.selectedTags, control) } : null));
  }

  function toggleEditControl(control: ControlOption) {
    setEditModal((prev) => (prev ? { ...prev, selectedTags: toggleTag(prev.selectedTags, control) } : null));
  }

  const linkedDoc = documentParam && documents
    ? documents.find(d => d.id === documentParam || d.documentGroupId === documentParam || d.versions.some(v => v.id === documentParam)) ?? null
    : null;
  useDeepLinkScroll(linkedDoc?.id, activeTab === "library");

  if (!documents) {
    return <LoadingBlock />;
  }

  // Both tabs read this: the evidence gathered at the chosen site, or only the
  // organisation-wide evidence, or everything.
  const siteVisible = siteFilter === "all"
    ? documents
    : documents.filter(doc => siteFilter === "none" ? !doc.siteId : doc.siteId === siteFilter);

  const isUploadBusy =
    uploadModal?.step === "uploading" ||
    uploadModal?.step === "suggesting" ||
    uploadModal?.step === "confirming";

  return (
    <div className="page-pad">
      <style>{`
        @keyframes doc-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .doc-spinner { animation: doc-spin 0.8s linear infinite; }
      `}</style>

      {/* Hidden file input — used for both new uploads and new versions */}
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) startUpload(f, newVersionTargetRef.current ?? undefined);
          newVersionTargetRef.current = null;
          e.target.value = "";
        }}
      />

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) startUpload(f);
          e.target.value = "";
        }}
      />

      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>
          Evidence
        </h1>
        <p style={{ margin: 0, fontSize: 13, color: theme.color.subtle }}>
          Upload evidence files and tag them against the controls they support. Evidence is checked by the AI during compliance checks.
        </p>
      </div>

      {/* Import from connected storage — renders nothing until a source is connected */}
      <EvidenceImportPanel onImported={load} />
      <QualityReviewSettingsLine settings={qualitySettings} canAdmin={canAdminOrg} onChange={setQualitySettings} />

      {/* Tab bar */}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 8, marginBottom: 20, boxShadow: "inset 0 -1px 0 var(--c-line)" }}>
        <IndexTabs
          items={[
            { id: "library", label: "All Documents" },
            { id: "repository", label: "By Standard" },
          ]}
          value={activeTab}
          onChange={setActiveTab}
          style={{ flex: 1, minWidth: 0, marginBottom: 0 }}
        />
        {sites.length > 0 && documents && documents.length > 0 && (
          <select
            value={siteFilter}
            onChange={e => setSiteFilter(e.target.value)}
            title="Evidence gathered at one site"
            style={{ ...inputStyle, width: "auto", marginBottom: 6, padding: "4px 8px", fontSize: 12, ...(siteFilter !== "all" ? { borderColor: theme.color.accent, color: theme.color.accentText } : {}) }}
          >
            <option value="all">All offices</option>
            {sites.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
            <option value="none">Organisation-wide only</option>
          </select>
        )}
      </div>

      {error && (
        <div style={{ fontSize: 12, color: theme.color.dangerText, marginBottom: 16 }}>{error}</div>
      )}
      {notice && (
        <div style={{ fontSize: 12, color: theme.color.successText, marginBottom: 16 }}>{notice}</div>
      )}

      {activeTab === "repository" ? (
        /* ── Evidence Repository: docs grouped by enrolled standard ── */
        <EvidenceRepositoryView
          canWrite={canWriteDocs}
          documents={siteVisible}
          frameworks={frameworks.map((f) => f.frameworkId)}
          onDownload={downloadDoc}
          onDelete={deleteDoc}
          onNewVersion={(doc) => { newVersionTargetRef.current = doc; fileInputRef.current?.click(); }}
          onEditTags={(doc) => setEditModal({ doc, selectedTags: doc.controls, controlSearch: "", controlFramework: "", saving: false })}
          onUploadForFramework={(frameworkId) => {
            newVersionTargetRef.current = null;
            uploadFrameworkRef.current = frameworkId;
            fileInputRef.current?.click();
          }}
        />
      ) : (
        <>
      {/* Drop zone */}
      {canWriteDocs ? (
      <div
        style={{
          ...emptyFrame,
          border: dragOver ? `2px dashed ${theme.color.accent}` : emptyFrame.border,
          padding: documents.length === 0 ? "60px 40px" : "24px 32px",
          cursor: "pointer",
          marginBottom: documents.length > 0 ? 16 : 0,
          background: dragOver ? `${theme.color.accentBg}40` : emptyFrame.background,
          transition: "border-color 0.15s, background 0.15s",
        }}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer.files[0];
          uploadFrameworkRef.current = "";
          if (f) startUpload(f);
        }}
        onClick={() => { newVersionTargetRef.current = null; uploadFrameworkRef.current = ""; fileInputRef.current?.click(); }}
      >
        <Paperclip size={documents.length === 0 ? 32 : 20} color="var(--c-muted)" />
        <div style={{ fontSize: 13, color: theme.color.muted, marginTop: 8 }}>
          Drag and drop evidence files here
        </div>
        <div style={{ fontSize: 12, color: theme.color.accent, marginTop: 4 }}>or click to browse</div>
        {supportsCapture && (
          <button
            type="button"
            style={{ ...secondaryBtn, marginTop: 10 }}
            onClick={(e) => { e.stopPropagation(); cameraInputRef.current?.click(); }}
          >
            <Camera size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />Take a photo as evidence
          </button>
        )}
      </div>
      ) : documents.length === 0 ? (
        <div style={{ ...emptyFrame, padding: "40px 32px", fontSize: 13, color: theme.color.muted }}>
          No evidence has been added yet. Members who manage documents add it here.
        </div>
      ) : null}

      {/* Dept filter */}
      {documents.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
          {/* The evidence's office is filtered by the select above (with "Organisation-wide only"); one filter, not two. */}
          <EntityTagFilters value={tagFilter} onChange={setTagFilter} dimensions={["department", "jurisdiction", "type"]} />
        </div>
      )}

      {/* Document list */}
      {documents.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {siteVisible.filter(doc => matchesEntityTagFilter(tagData, entityRef({ kind: "document", id: doc.id }), tagFilter)).map((doc) => {
            const totalVersions = doc.versions.length + 1; // history + current
            const isHistoryOpen = historyOpen === doc.documentGroupId;

            return (
              <div key={doc.id} data-deeplink={doc.id} style={{ ...card, padding: "14px 18px" }}>
                {/* Main row */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                  {/* Left: name, meta, control chips */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>
                        {doc.fileName}
                      </span>
                      {totalVersions > 1 && (
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 3,
                          background: theme.color.accentBg, color: theme.color.accentText,
                          border: `1px solid ${theme.color.accent}`,
                        }}>
                          v{doc.version}
                        </span>
                      )}
                      <EvidenceSourceBadge source={doc.source} webUrl={doc.sourceWebUrl} />
                      <ValidityChip validity={doc.validity} />
                      <QualityChip review={doc.qualityReview} />
                    </div>
                    {doc.validity && (
                      <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>
                        {doc.validity.typeLabel}, dated {fmtDateOnly(doc.validity.evidenceDate)}
                        {" · Renewed by "}
                        {doc.renewalOwner ? `${doc.renewalOwner.displayName || doc.renewalOwner.email}${doc.renewalOwner.isActive ? "" : " (left)"}` : "nobody named"}
                        {doc.validity.standing === "lapsed" && " · Stays on file; no longer counts as current evidence"}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 2 }}>
                      {fmtBytes(doc.sizeBytes)} · {new Date(doc.uploadedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                      {totalVersions > 1 && (
                        <button
                          onClick={() => setHistoryOpen(isHistoryOpen ? null : doc.documentGroupId)}
                          style={{ background: "none", border: "none", color: theme.color.accentText, fontSize: 11, cursor: "pointer", marginLeft: 8, padding: 0 }}
                        >
                          {isHistoryOpen ? "Hide history" : `${totalVersions} versions`}
                        </button>
                      )}
                    </div>
                    <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {doc.controls.length > 0 ? (
                        doc.controls.map((c) => <TagChip key={tagKey(c.frameworkId, c.controlRef)} tag={c} legal={legalRefs.get(c.controlRef)} />)
                      ) : (
                        <span style={{ fontSize: 11, color: theme.color.faint }}>No controls tagged</span>
                      )}
                    </div>
                    {/* Advice beside the tags: does the document evidence them? Never changes a tag. */}
                    {(!doc.source || ["upload", "m365", "onedrive", "sharepoint", "google_drive"].includes(doc.source)) && (
                      <QualityReviewPanel documentId={doc.id} review={doc.qualityReview} tags={doc.controls} canWrite={canWriteDocs} onChange={load} />
                    )}
                    {/* The department picker and the separate "site this was
                        gathered at" dropdown were two ways of asking the same two
                        questions. One row now asks all four, and its Office
                        section writes the document's own siteId — the column the
                        site detail page already reads. */}
                    <EntityTagsRow entity={{ kind: "document", id: doc.id }} style={{ marginTop: 6, marginBottom: 0 }} />
                    <div style={{ marginTop: 6 }}>
                      <RelatedRecords entity={{ kind: "document", id: doc.id }} links={documentLinks(doc)} defaultOpen={documentParam === doc.id} />
                    </div>
                  </div>

                  {/* Right: actions */}
                  <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    {canWriteDocs && (
                    <button
                      style={secondaryBtn}
                      onClick={() => {
                        newVersionTargetRef.current = doc;
                        fileInputRef.current?.click();
                      }}
                      title="Upload a new version of this document"
                    >
                      New version
                    </button>
                    )}
                    {canWriteDocs && (
                    <button
                      style={secondaryBtn}
                      onClick={() => setEditModal({ doc, selectedTags: doc.controls, controlSearch: "", controlFramework: "", saving: false })}
                    >
                      Edit tags
                    </button>
                    )}
                    {canWriteDocs && (
                    <button style={secondaryBtn} onClick={() => openValidity(doc)} title="Evidence type, date and who renews it">
                      Validity
                    </button>
                    )}
                    <button style={secondaryBtn} onClick={() => downloadDoc(doc.id)}>
                      Download
                    </button>
                    {/* Only an uploaded document is mirrored out; an imported one is
                        read from its source, so pushing it back would only re-import it. */}
                    {canResync && mirrorTarget && (doc.source ?? "upload") === "upload" && (
                      <button
                        style={{ ...secondaryBtn, opacity: resyncing === doc.id ? 0.6 : 1 }}
                        disabled={resyncing === doc.id}
                        onClick={() => resyncDoc(doc)}
                        title={`Copy this document to ${mirrorTarget.label} again`}
                      >
                        {resyncing === doc.id ? "Re-syncing…" : "Re-sync"}
                      </button>
                    )}
                    {canWriteDocs && (
                    <button style={dangerBtn} onClick={() => deleteDoc(doc)}>
                      Delete
                    </button>
                    )}
                  </div>
                </div>

                {/* Version history panel */}
                {isHistoryOpen && doc.versions.length > 0 && (
                  <div style={{ marginTop: 12, borderTop: `1px solid ${theme.color.border}`, paddingTop: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                      Version history
                    </div>
                    {doc.versions.map((v) => (
                      <div
                        key={v.id}
                        style={{
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          padding: "6px 0", borderBottom: `1px solid ${theme.color.border}`, gap: 12,
                        }}
                      >
                        <div style={{ fontSize: 12, color: theme.color.subtle }}>
                          <span style={{
                            fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: 3,
                            background: theme.color.panelAlt, color: theme.color.faint,
                            border: `1px solid ${theme.color.border}`, marginRight: 8,
                          }}>
                            v{v.version}
                          </span>
                          {v.fileName}
                          <span style={{ color: theme.color.faint, marginLeft: 8 }}>
                            {fmtBytes(v.sizeBytes)} · {new Date(v.uploadedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                          </span>
                        </div>
                        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                          <button style={{ ...secondaryBtn, padding: "3px 8px", fontSize: 11 }} onClick={() => downloadDoc(v.id)}>
                            Download
                          </button>
                          {canWriteDocs && (
                          <button
                            style={{ ...dangerBtn, padding: "3px 8px", fontSize: 11 }}
                            onClick={() => deleteVersion(v, doc.fileName)}
                          >
                            Delete
                          </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
        </>
      )}

      {/* Upload modal */}
      {uploadModal && (
        <div style={overlayStyle}>
          <div style={modalCard}>
            {(uploadModal.step === "uploading" || uploadModal.step === "suggesting") ? (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <Spinner />
                <div style={{ marginTop: 12, color: theme.color.muted, fontSize: 13 }}>
                  {uploadModal.step === "uploading" ? "Uploading file…" : "Getting AI control suggestions…"}
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: theme.color.heading, marginBottom: 4, wordBreak: "break-all" }}>
                  {uploadModal.newVersionOf ? "Upload new version" : "Tag controls"}
                </div>
                <div style={{ fontSize: 12, color: theme.color.faint, marginBottom: 16 }}>
                  {uploadModal.file.name}
                </div>

                {uploadModal.newVersionOf && uploadModal.inheritedTags && uploadModal.inheritedTags.length > 0 && (
                  <div style={{ marginBottom: 12, fontSize: 12, color: theme.color.subtle }}>
                    Control tags from the previous version are pre-selected. Adjust as needed.
                  </div>
                )}

                {/* AI suggestions */}
                {!uploadModal.newVersionOf && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted, marginBottom: 8 }}>
                      AI suggested controls
                    </div>
                    {uploadModal.error && uploadModal.suggestedTags.length === 0 ? (
                      <div style={{ fontSize: 12, color: theme.color.dangerText }}>{uploadModal.error}</div>
                    ) : uploadModal.suggestedTags.length > 0 ? (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {uploadModal.suggestedTags.map((tag) => (
                          <TagChip key={tagKey(tag.frameworkId, tag.controlRef)} tag={tag} legal={legalRefs.get(tag.controlRef)} />
                        ))}
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: theme.color.faint }}>No suggestions — select controls manually.</div>
                    )}
                  </div>
                )}

                {/* Validity (backend lib/evidenceValidity.ts): a new version starts a new period from its own date. */}
                {(!uploadModal.newVersionOf || uploadModal.inheritedType) && (
                  <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid var(--c-line)" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted, marginBottom: 8 }}>
                      {uploadModal.newVersionOf ? "Date of this version" : "Validity"}
                    </div>
                    <ValidityFields
                      value={uploadModal.validity}
                      onChange={(next) => setUploadModal((prev) => (prev ? { ...prev, validity: next } : null))}
                      types={evidenceTypes.types}
                      members={renewalOwners}
                      suggestion={uploadModal.validitySuggestion}
                      typeLocked={!!uploadModal.newVersionOf}
                      showOwner={!uploadModal.newVersionOf}
                    />
                  </div>
                )}

                <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted, marginBottom: 8 }}>
                  All controls
                </div>
                <ControlList
                  controls={controlOptions}
                  selectedKeys={new Set(uploadModal.selectedTags.map((t) => tagKey(t.frameworkId, t.controlRef)))}
                  onToggle={toggleUploadControl}
                  search={uploadModal.controlSearch}
                  onSearchChange={(v) => setUploadModal((prev) => prev ? { ...prev, controlSearch: v } : null)}
                  framework={uploadModal.controlFramework}
                  onFrameworkChange={(v) => setUploadModal((prev) => prev ? { ...prev, controlFramework: v } : null)}
                />

                {uploadModal.error && uploadModal.suggestedTags.length > 0 && (
                  <div style={{ fontSize: 12, color: theme.color.dangerText, marginTop: 8 }}>{uploadModal.error}</div>
                )}

                <div style={{ display: "flex", gap: 8, marginTop: 20, justifyContent: "flex-end" }}>
                  <button style={secondaryBtn} onClick={() => setUploadModal(null)} disabled={isUploadBusy}>
                    Cancel
                  </button>
                  <button style={secondaryBtn} onClick={() => confirmUpload([])} disabled={isUploadBusy}>
                    Skip tagging
                  </button>
                  <button
                    style={{ ...btnPrimary, opacity: isUploadBusy ? 0.7 : 1, cursor: isUploadBusy ? "default" : "pointer" }}
                    onClick={() => confirmUpload(uploadModal.selectedTags)}
                    disabled={isUploadBusy}
                  >
                    {uploadModal.step === "confirming" ? "Saving…" : uploadModal.newVersionOf ? "Save new version" : "Confirm upload"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Validity modal */}
      {validityModal && (
        <div style={overlayStyle}>
          <div role="dialog" aria-label="Evidence validity" style={{ ...modalCard, maxWidth: 680 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: theme.color.heading, marginBottom: 6, paddingBottom: 10, borderBottom: "1px solid var(--c-line)", wordBreak: "break-all" }}>
              Validity — {validityModal.doc.fileName}
            </div>
            <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 12 }}>
              A dated record (a test, scan, review, certificate or schedule) counts as current evidence for a set period. When it runs out the document stays on file, labelled out of date, and the person who renews it is asked for the newer one.
            </div>
            <ValidityFields
              value={validityModal.draft}
              onChange={(next) => setValidityModal((prev) => (prev ? { ...prev, draft: next } : null))}
              types={evidenceTypes.types}
              members={renewalOwners}
            />
            {validityModal.error && (
              <div role="alert" style={{ marginTop: 12, fontSize: 12, color: theme.color.dangerText }}>{validityModal.error}</div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 20, justifyContent: "flex-end" }}>
              <button style={secondaryBtn} onClick={() => setValidityModal(null)} disabled={validityModal.saving}>Cancel</button>
              <button
                style={{ ...btnPrimary, opacity: validityModal.saving ? 0.7 : 1, cursor: validityModal.saving ? "default" : "pointer" }}
                onClick={saveValidity}
                disabled={validityModal.saving}
              >
                {validityModal.saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit tags modal */}
      {editModal && (
        <div style={overlayStyle}>
          <div style={{ ...modalCard, maxWidth: 480 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: theme.color.heading, marginBottom: 16, paddingBottom: 10, borderBottom: "1px solid var(--c-line)", wordBreak: "break-all" }}>
              Edit control tags — {editModal.doc.fileName}
            </div>
            <ControlList
              controls={controlOptions}
              selectedKeys={new Set(editModal.selectedTags.map((t) => tagKey(t.frameworkId, t.controlRef)))}
              onToggle={toggleEditControl}
              search={editModal.controlSearch}
              onSearchChange={(v) => setEditModal((prev) => prev ? { ...prev, controlSearch: v } : null)}
              framework={editModal.controlFramework}
              onFrameworkChange={(v) => setEditModal((prev) => prev ? { ...prev, controlFramework: v } : null)}
            />
            {editModal.error && (
              <div role="alert" style={{ marginTop: 12, fontSize: 12, color: theme.color.dangerText }}>{editModal.error}</div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 20, justifyContent: "flex-end" }}>
              <button style={secondaryBtn} onClick={() => setEditModal(null)} disabled={editModal.saving}>
                Cancel
              </button>
              <button
                style={{ ...btnPrimary, opacity: editModal.saving ? 0.7 : 1, cursor: editModal.saving ? "default" : "pointer" }}
                onClick={saveEditModal}
                disabled={editModal.saving}
              >
                {editModal.saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
